import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Plus } from 'lucide-react';
import { Material, MaterialCategory, createMaterial, updateMaterial, getMaterialCategories, createMaterialCategory } from '../lib/materials';

const UNIT_OPTIONS = [
    { value: 'foot', label: 'Foot (ft)', group: 'Length' },
    { value: 'meter', label: 'Meter (m)', group: 'Length' },
    { value: 'square_foot', label: 'Square Foot (sq ft)', group: 'Area' },
    { value: 'square_meter', label: 'Square Meter (sq m)', group: 'Area' },
    { value: 'gallon', label: 'Gallon (gal)', group: 'Volume' },
    { value: 'half_gallon', label: 'Half Gallon (½ gal)', group: 'Volume' },
    { value: 'quart', label: 'Quart (qt)', group: 'Volume' },
    { value: 'liter', label: 'Liter (L)', group: 'Volume' }
];

interface CostCalculator {
  total_cost: string;
  length?: string;
  width?: string;
  volume?: string;
}

interface MaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
  item?: Material | null;
  mode: 'create' | 'edit';
}

export default function MaterialModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  businessId,
  item,
  mode 
}: MaterialModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [useCostCalculator, setUseCostCalculator] = useState(false);
  const [calculator, setCalculator] = useState<CostCalculator>({
    total_cost: '',
    length: '',
    width: '',
    volume: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cost: '',
    unit: UNIT_OPTIONS[0].value,
    category_id: ''
  });

  // Calculate cost based on calculator inputs
  const calculateCost = () => {
    const totalCost = parseFloat(calculator.total_cost) || 0;
    
    // Get unit type from selected unit
    const selectedUnit = UNIT_OPTIONS.find(u => u.value === formData.unit);
    const unitGroup = selectedUnit?.group;
    
    if (!totalCost) return;
    
    let calculatedCost = 0;
    
    switch (unitGroup) {
      case 'Length':
        const length = parseFloat(calculator.length || '0');
        if (length) {
          calculatedCost = totalCost / length;
        }
        break;
        
      case 'Area':
        const areaLength = parseFloat(calculator.length || '0');
        const width = parseFloat(calculator.width || '0');
        if (areaLength && width) {
          const area = areaLength * width;
          calculatedCost = totalCost / area;
        }
        break;
        
      case 'Volume':
        const volume = parseFloat(calculator.volume || '0');
        if (volume) {
          calculatedCost = totalCost / volume;
        }
        break;
    }
    
    if (calculatedCost > 0) {
      setFormData(prev => ({ ...prev, cost: calculatedCost.toFixed(2) }));
    }
  };

  // Update cost when calculator values change
  useEffect(() => {
    if (useCostCalculator) {
      calculateCost();
    }
  }, [calculator, formData.unit]);

  const loadCategories = async () => {
    if (!businessId) return;
    
    try {
      const data = await getMaterialCategories(businessId);
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCategories();
      if (mode === 'edit' && item) {
        setFormData({
          name: item.name,
          description: item.description || '',
          cost: item.cost.toString(),
          unit: item.unit,
          category_id: item.category_id || ''
        });
      } else {
        // Reset form for create mode
        setFormData({
          name: '',
          description: '',
          cost: '',
          unit: UNIT_OPTIONS[0].value,
          category_id: ''
        });
      }
      
      // Auto-focus on name field
      setTimeout(() => {
        const nameInput = document.querySelector('[data-material-name-input]') as HTMLInputElement;
        if (nameInput) {
          nameInput.focus();
        }
      }, 100);
    }
  }, [isOpen, item, mode]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        name: '',
        description: '',
        cost: '',
        unit: UNIT_OPTIONS[0].value,
        category_id: ''
      });
      setError(null);
      setUseCostCalculator(false);
      setCalculator({
        total_cost: '',
        length: '',
        width: '',
        volume: ''
      });
    }
  };

  const handleAddCategory = async () => {
    if (!businessId || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createMaterialCategory({
        business_id: businessId,
        name: newCategoryName.trim(),
        description: '',
        order_index: categories.length
      });
      
      await loadCategories();
      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      setError('Material name is required');
      return;
    }

    const cost = parseFloat(formData.cost);
    if (isNaN(cost) || cost < 0) {
      setError('Please enter a valid cost');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean up category_id - convert empty string to null
      const cleanedFormData = {
        ...formData,
        name: formData.name.trim(),
        category_id: formData.category_id || null,
        cost
      };

      if (mode === 'create') {
        await createMaterial({
          business_id: businessId,
          ...cleanedFormData
        });
      } else if (mode === 'edit' && item) {
        await updateMaterial(item.id, cleanedFormData);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving material:', err);
      setError(err instanceof Error ? err.message : 'Failed to save material');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              {mode === 'create' ? 'New Material' : 'Edit Material'}
            </h2>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* Material Information */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Material Name *
                    </label>
                    <input
                      type="text"
                      required
                      data-material-name-input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter material name"
                    />
                  </div>

                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category
                    </label>
                    {isAddingCategory ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                          placeholder="Enter category name"
                        />
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={handleAddCategory}
                            disabled={!newCategoryName.trim() || loading}
                            className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsAddingCategory(false);
                              setNewCategoryName('');
                            }}
                            className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <select
                          value={formData.category_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">No Category</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setIsAddingCategory(true)}
                          className="inline-flex items-center px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-4 w-4 mr-1.5" />
                          New Category
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description (optional)
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter material description"
                    />
                  </div>
                </div>
              </div>

              {/* Cost & Unit Information */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cost Method
                    </label>
                    <div className="flex space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!useCostCalculator}
                          onChange={() => setUseCostCalculator(false)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Direct Cost</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={useCostCalculator}
                          onChange={() => setUseCostCalculator(true)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Calculator</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Unit
                    </label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                    >
                      {UNIT_OPTIONS.map((unit, index) => (
                        <React.Fragment key={unit.value}>
                          {(index === 0 || UNIT_OPTIONS[index - 1].group !== unit.group) && (
                            <optgroup label={unit.group}>
                              <option value={unit.value}>{unit.label}</option>
                            </optgroup>
                          )}
                          {(index > 0 && UNIT_OPTIONS[index - 1].group === unit.group) && (
                            <option value={unit.value}>{unit.label}</option>
                          )}
                        </React.Fragment>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Cost Input Section */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                {useCostCalculator ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Total Cost
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500" style={{ fontSize: '16px' }}>$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={calculator.total_cost}
                          onChange={(e) => setCalculator(prev => ({ ...prev, total_cost: e.target.value }))}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                        />
                      </div>
                    </div>
                    
                    {/* Length/Area Fields */}
                    {(formData.unit.includes('foot') || formData.unit.includes('meter')) && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Length
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={calculator.length}
                            onChange={(e) => setCalculator(prev => ({ ...prev, length: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                          />
                        </div>
                        
                        {(formData.unit.includes('square')) && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Width
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={calculator.width}
                              onChange={(e) => setCalculator(prev => ({ ...prev, width: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Volume Field */}
                    {(formData.unit.includes('gallon') || formData.unit.includes('liter') || formData.unit.includes('quart')) && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Volume
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={calculator.volume}
                          onChange={(e) => setCalculator(prev => ({ ...prev, volume: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                        />
                      </div>
                    )}
                    
                    {/* Calculated Cost Display */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="text-sm font-semibold text-blue-900">
                        Calculated Cost: ${formData.cost || '0.00'} per {formData.unit}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Direct Cost *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <span className="text-gray-500" style={{ fontSize: '16px' }}>$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={formData.cost}
                        onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={`${isMobile ? 'p-4' : 'p-6'} pt-0`}>
              <button
                type="submit"
                disabled={loading || !formData.name.trim() || !formData.cost}
                className="w-full px-4 py-3 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create Material' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // Use React portal to render modal at document.body level for proper z-index
  return ReactDOM.createPortal(modalContent, document.body);
}